
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowUpCircle, ArrowDownCircle, AlertCircle } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { sanitizeInput } from "@/utils/security";

interface TransactionActionsProps {
  balance: string;
  onDeposit: (amount: string) => void;
  onWithdraw: (amount: string) => void;
}

export const TransactionActions = ({ balance, onDeposit, onWithdraw }: TransactionActionsProps) => {
  const { toast } = useToast();
  const [depositAmount, setDepositAmount] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [depositError, setDepositError] = useState("");
  const [withdrawError, setWithdrawError] = useState("");

  // Clear errors when amounts change
  useEffect(() => {
    if (depositAmount) setDepositError("");
  }, [depositAmount]);

  useEffect(() => {
    if (withdrawAmount) setWithdrawError("");
  }, [withdrawAmount]);

  const validateAndDeposit = () => {
    // Validate deposit amount
    if (!depositAmount) {
      setDepositError("Please enter an amount");
      return;
    }

    const amount = parseFloat(depositAmount);
    if (isNaN(amount) || amount <= 0) {
      setDepositError("Please enter a valid positive amount");
      return;
    }

    // Sanitize input before processing
    const sanitizedAmount = sanitizeInput(depositAmount);
    onDeposit(sanitizedAmount);
    setDepositAmount("");
  };

  const validateAndWithdraw = () => {
    // Validate withdrawal amount
    if (!withdrawAmount) {
      setWithdrawError("Please enter an amount");
      return;
    }

    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount <= 0) {
      setWithdrawError("Please enter a valid positive amount");
      return;
    }

    if (amount > parseFloat(balance)) {
      setWithdrawError("Insufficient balance for withdrawal");
      return;
    }

    // Sanitize input before processing
    const sanitizedAmount = sanitizeInput(withdrawAmount);
    onWithdraw(sanitizedAmount);
    setWithdrawAmount("");
  };

  return (
    <Card className="border-emerald-100/60 shadow-md hover:shadow-lg transition-all">
      <CardHeader className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-t-lg">
        <CardTitle className="text-emerald-700">Quick Actions</CardTitle>
        <CardDescription>Deposit or withdraw funds</CardDescription>
      </CardHeader>
      <CardContent className="p-6">
        <Tabs defaultValue="deposit" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="deposit" className="data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-800">
              Deposit
            </TabsTrigger>
            <TabsTrigger value="withdraw" className="data-[state=active]:bg-blue-100 data-[state=active]:text-blue-800">
              Withdraw
            </TabsTrigger>
          </TabsList>
          <TabsContent value="deposit">
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-full space-y-1">
                  <Input
                    type="number"
                    placeholder="Amount in ETH"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    className={depositError ? "border-red-300 focus-visible:ring-red-400" : ""}
                  />
                  {depositError && <p className="text-xs text-red-500">{depositError}</p>}
                </div>
                <Button onClick={validateAndDeposit} variant="success">
                  <ArrowUpCircle className="w-4 h-4 mr-2" />
                  Deposit
                </Button>
              </div>
              <p className="text-sm text-gray-500 flex items-start">
                <AlertCircle className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
                <span>Deposits may take a few minutes to process</span>
              </p>
            </div>
          </TabsContent>
          <TabsContent value="withdraw">
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-full space-y-1">
                  <Input
                    type="number"
                    placeholder="Amount in ETH"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    className={withdrawError ? "border-red-300 focus-visible:ring-red-400" : ""}
                  />
                  {withdrawError && <p className="text-xs text-red-500">{withdrawError}</p>}
                </div>
                <Button onClick={validateAndWithdraw} variant="success">
                  <ArrowDownCircle className="w-4 h-4 mr-2" />
                  Withdraw
                </Button>
              </div>
              <p className="text-sm text-gray-500 flex items-start">
                <AlertCircle className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
                <span>Maximum withdrawal: {balance} ETH</span>
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
